import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';
import { EventBadges } from '@Components/TicketedEvents/Badges';
import { useTicketedEvent } from '@Hooks/useTicketedEvent';
import { cn } from '@Utils/cn';
import { formatCurrency, formatZonedDateTime } from '@Utils/utils';
import {
  ArrowRight,
  ArrowUpRight,
  Calendar,
  MapPin,
  Ticket,
} from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { eventTypeToTitleMap } from './constants';
import { CloudinaryImage } from '@art-of-hospitality-solutions-llc/ui-kit/components/cloudinary-image';
import RichText from '@Components/RichText';

export function EventDialog({
  open,
  onOpenChange,
  eventId,
  eventType,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string | null;
  eventType: 'ticketed' | 'private-dining' | null;
}) {
  const { data, status } = useTicketedEvent({
    eventId,
    include:
      'venue,featuredImage,images,ticketTypes,tickets,categories,tags,organizers',
    enabled: !!eventId && open,
  });

  const ticketTypes = data?.ticketTypes ?? [];
  const tickets = data?.tickets ?? [];

  // Calculate tickets sold per ticket type
  const ticketTypeStats = useMemo(() => {
    return ticketTypes
      .filter((tt) => !tt.deletedAt)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((ticketType) => {
        const soldCount = tickets.filter(
          (t) => t.ticketTypeId === ticketType.id && !t.deletedAt,
        ).length;
        return {
          ...ticketType,
          sold: soldCount,
          remaining: ticketType.available - soldCount,
        };
      });
  }, [ticketTypes, tickets]);

  // Calculate totals
  const totalCapacity = ticketTypeStats.reduce(
    (sum, tt) => sum + tt.available,
    0,
  );
  const totalSold = ticketTypeStats.reduce((sum, tt) => sum + tt.sold, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('flex max-h-[95vh] max-w-4xl! flex-col overflow-hidden')}
      >
        <DialogHeader className="pb-2">
          {eventType ? eventTypeToTitleMap[eventType] : 'Event'}
        </DialogHeader>
        <div className="grow overflow-auto">
          {status === 'pending' ? (
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-3 w-60" />
              <Skeleton className="h-48" />
              <Skeleton className="h-4 w-40" />
            </div>
          ) : !data ? (
            <div className="text-sm text-zinc-500">No event found.</div>
          ) : (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-2xl font-semibold">{data.name}</div>
                  <div className="flex flex-wrap gap-2">
                    <EventBadges event={data} />
                  </div>
                </div>
                <div>
                  <Button
                    asChild
                    variant="link"
                    size="sm"
                    className="px-0"
                    icon={{
                      src: <ArrowUpRight />,
                      location: 'right',
                    }}
                  >
                    <Link to={`/ticketed-events/${data.id}`} target="_blank">
                      View in new tab
                    </Link>
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-8">
                <div className="col-span-3">
                  <div className="space-y-3 rounded-xl border border-zinc-200 bg-white p-4">
                    <div className="flex items-start gap-2">
                      <div className="my-1">
                        <Calendar size={16} className="text-[#caab6a]" />
                      </div>
                      <div>
                        {formatZonedDateTime(
                          data.startDateTime,
                          'MMMM dd, yyyy',
                        )}
                        <br />
                        <span className="text-sm text-zinc-600">
                          {formatZonedDateTime(data.startDateTime, 'hh:mm a')}
                          <ArrowRight
                            size={12}
                            className="mx-1 mb-0.5 inline"
                          />
                          {formatZonedDateTime(data.endDateTime, 'hh:mm a')}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="my-1">
                        <MapPin size={16} className="text-[#caab6a]" />
                      </div>
                      <div>
                        {data.venue?.name
                          ? data.venue.name.toUpperCase()
                          : 'Venue not assigned yet'}
                      </div>
                    </div>
                  </div>

                  {/* Ticket Types Section */}
                  <div className="mt-4">
                    <div className="mb-3 flex items-center gap-2">
                      <Ticket size={18} className="text-[#caab6a]" />
                      <h3 className="text-lg font-semibold">Ticket Types</h3>
                      <span className="text-sm text-zinc-500">
                        ({totalSold} / {totalCapacity} sold)
                      </span>
                    </div>

                    {ticketTypeStats.length === 0 ? (
                      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-center text-sm text-zinc-500">
                        No ticket types configured
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {ticketTypeStats.map((ticketType) => (
                          <div
                            key={ticketType.id}
                            className="rounded-lg border border-zinc-200 bg-white p-3"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">
                                  {ticketType.name}
                                </div>
                                {ticketType.description && (
                                  <div className="text-sm text-zinc-500">
                                    {ticketType.description}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <div className="font-semibold">
                                  {formatCurrency(ticketType.price)}
                                </div>
                              </div>
                            </div>
                            <div className="mt-2 flex items-center justify-between text-sm">
                              <div className="flex gap-4">
                                <span className="text-zinc-600">
                                  <span className="font-medium text-zinc-900">
                                    {ticketType.sold}
                                  </span>{' '}
                                  sold
                                </span>
                                <span className="text-zinc-600">
                                  <span className="font-medium text-zinc-900">
                                    {ticketType.remaining}
                                  </span>{' '}
                                  remaining
                                </span>
                              </div>
                              <div className="text-zinc-500">
                                of {ticketType.available} available
                              </div>
                            </div>
                            {/* Progress bar */}
                            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-zinc-100">
                              <div
                                className={cn(
                                  'h-full rounded-full transition-all',
                                  ticketType.remaining === 0
                                    ? 'bg-red-500'
                                    : ticketType.sold / ticketType.available >
                                        0.8
                                      ? 'bg-amber-500'
                                      : 'bg-emerald-500',
                                )}
                                style={{
                                  width: `${Math.min((ticketType.sold / ticketType.available) * 100, 100)}%`,
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-span-2">
                  {data.featuredImage && (
                    <div className="mb-3 flex min-h-42 items-center justify-center overflow-hidden rounded-md border border-zinc-200 bg-zinc-100">
                      <CloudinaryImage
                        publicId={data.featuredImage.cloudinaryId}
                        mode="original"
                        className="h-64 w-full object-cover"
                      />
                    </div>
                  )}
                  <RichText content={data.description} />
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mx-auto mt-8">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
